import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {addItemsToGrid} from '@Components/base-grid';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';
import type {LazyLoadingGetterParams, LazyLoadingResponse} from '@Libraries/pmw-stock-media-library';
import {openErrorModal} from '@Modals/error-modal';

interface SkeletonGridItemDimensions {
  id: string;
  width: number;
  height: number;
}

export const buildSkeletonGridItem = (skeleton: SkeletonGridItemDimensions): GridItemStorage => ({
  type: GRID_ITEM_TYPE.CUSTOM,
  id: skeleton.id,
  width: skeleton.width,
  height: skeleton.height,
  children: <SkeletonBrick width="100%" height="100%" radius="8px" theme={SkeletonBrickTheme.AI_ROTATING_GRADIENT} isPulsating />,
});

export interface GeneratedMediaGetItemsConfig<T extends {id: number}> {
  gridId: string;
  fetchItems: (limit: number, offset: number) => Promise<T[]>;
  buildGridItem: (item: T) => GridItemStorage;
  getGeneratingSkeletons: () => GridItemStorage[];
  onItemsLoaded: (items: T[]) => void;
}

export const makeGeneratedMediaGetItems = <T extends {id: number}>(config: GeneratedMediaGetItemsConfig<T>): ((params: LazyLoadingGetterParams) => Promise<LazyLoadingResponse>) => {
  return async (params: LazyLoadingGetterParams): Promise<LazyLoadingResponse> => {
    const offset = Object.values(params.offsets).reduce((agg, value) => agg + value, 0);
    let response: T[] = [];
    try {
      response = await config.fetchItems(params.limit, offset);
    } catch (error) {
      console.error('Error loading generated media grid items:', error);
      openErrorModal({message: window.i18next.t('pmwjs_internet_error_message'), log: false});
    }
    config.onItemsLoaded(response);
    if (offset === 0) {
      const skeletons = config.getGeneratingSkeletons();
      if (skeletons.length > 0) {
        window.PMW.redux.store.dispatch(
          addItemsToGrid({
            id: config.gridId,
            items: skeletons,
            index: 0,
          })
        );
      }
      const existingGridItemIds = new Set(window.PMW.redux.store.getState().grids.gridsHashmap[config.gridId]?.items.map((item) => item.id) ?? []);
      response = response.filter((item) => !existingGridItemIds.has(`${item.id}`));
    }
    return {
      response: response.map(config.buildGridItem),
      offsetsData: {DEFAULT_SOURCE: response.length},
    };
  };
};
